import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { createTheme, StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
// eslint-disable-next-line import/no-unresolved -- CRA's import resolver doesn't read the package's `exports` map
import { ObservabilityProvider } from "@hyly/observability/react";

import store from "./store";
import { injectConstStore, injectStore } from "./utils";
import reportWebVitals from "./reportWebVitals";
import ObservabilityIdentity from "./components/ObservabilityIdentity";
import ObservabilityFallback from "./components/ObservabilityFallback";
import { setupObservability } from "./observability";
import "./index.css";
import "./i18n";
import "./utils/favicon";

const App = React.lazy(() => import("./App"));

// Observability is PRODUCTION ONLY (config/security.env.production is the only env
// file with a DSN). setupObservability() self-guards on the DSN, and this flag gates
// the error boundary + identity component so dev/qa/stg render exactly as before.
const observabilityEnabled = Boolean(process.env.REACT_APP_SENTRY_DSN);

setupObservability();

const rootElement = document.getElementById("hayley-area");
const root = ReactDOM.createRoot(rootElement);

injectConstStore(store);
injectStore(store);

// All `Portal`-related components need to have the the main app wrapper element as a container
// so that the are in the subtree under the element used in the `important` option of the Tailwind's config.
const theme = createTheme({
  components: {
    MuiPopover: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiPopper: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiDialog: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiDrawer: {
      defaultProps: {
        container: rootElement,
      },
    },
  },
});

const appTree = (
  <Suspense fallback={null}>
    <App />
  </Suspense>
);

// The error boundary lives INSIDE the providers (not as the outermost wrapper) so its
// fallback can use Redux/theme/i18n and render a widget-styled crash screen. It is
// prod-gated: dev/qa/stg render `appTree` directly, byte-for-byte as before.
const tree = (
  <Provider store={store}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {observabilityEnabled && <ObservabilityIdentity />}
        {observabilityEnabled ? (
          <ObservabilityProvider fallback={({ resetError }) => <ObservabilityFallback resetError={resetError} />}>{appTree}</ObservabilityProvider>
        ) : (
          appTree
        )}
      </ThemeProvider>
    </StyledEngineProvider>
  </Provider>
);

root.render(<React.StrictMode>{tree}</React.StrictMode>);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
